import React, { useContext, useEffect, useRef, useState } from "react";
import pinPrompt from "../../utils/pinPrompt";
import { createScanDetector, dialogOpen, isRepeatScan, restoreField, snapshotField } from "../../utils/scanWedge";
import {
  KIOSK_DETECTOR, KioskScanContext, STRIP_MAX_ROWS, confirmPalletNoScan, kioskParse, palletScanState, palletsToScan,
  resultMs, sendScan, stopPalletLight,
} from "../../features/laserKiosk/kioskScan";
import { SurplusRefreshContext } from "../SurplusRack";

const TONE = {
  ok: { bg: "#12422a", border: "#3ddc84", color: "#d7ffe6" },
  info: { bg: "#1c2a3a", border: "#4ec9ff", color: "#d6efff" },
  warn: { bg: "#4a3a10", border: "#ffd969", color: "#fff3c4" },
  bad: { bg: "#7a0014", border: "#ff3b4e", color: "#ffffff" },
};

/**
 * The USB scanner on the laser kiosk (2026-10-30). ScanWedge is off on /laser/*, because a scan must never navigate
 * away from this display, and without a listener a scan's Enter clicks whatever button has focus and submits a
 * PIN prompt with the code's digits. So this listens to every keydown (window, capture phase) and:
 * - always swallows a scan's Enter and puts back the text field it typed into, switched on or off;
 * - while a dialog is open records nothing and says so (a PIN prompt waits for the person typing it);
 * - otherwise sends the code to POST /laser/scan and shows the API's answer. Red stays until the next scan or OK.
 * The same code is ignored until it has been out of the scanner's view for 3 s (the window slides), as in ScanWedge.
 */
export default function KioskScanner({ on, onDone, onPalletOk }) {
  const [result, setResult] = useState(null);
  const onRef = useRef(on);
  onRef.current = on;
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const palletOkRef = useRef(onPalletOk);
  palletOkRef.current = onPalletOk;
  const stateRef = useRef(null);
  if (stateRef.current === null) {
    stateRef.current = { detector: createScanDetector({ ...KIOSK_DETECTOR, parse: kioskParse }), snap: null, last: null, seq: 0 };
  }

  useEffect(() => {
    if (!result) return undefined;
    const ms = resultMs(result.tone);
    if (ms == null) return undefined;
    const id = setTimeout(() => setResult(null), ms);
    return () => clearTimeout(id);
  }, [result]);

  useEffect(() => {
    const s = stateRef.current;

    async function handle(code) {
      s.seq += 1;
      const mine = s.seq;
      setResult({ tone: "info", title: "Reading…", message: "" });
      let out;
      try {
        out = await sendScan(code);
      } catch (_) {
        if (s.last && s.last.code === code) s.last = null;      // or "scan it again" reads as still in view
        out = { tone: "bad", title: "NOT SAVED", message: "JobQ didn't get that scan. Scan it again." };
      }
      if (mine === s.seq) setResult(out);        // a later scan's answer wins
      if (out.outcome === "ok" && out.task_id && out.pallet_id) stopPalletLight(out.task_id, out.pallet_id);
      if ((out.outcome === "ok" || out.outcome === "already") && out.pallet_id && palletOkRef.current) palletOkRef.current();
      if (doneRef.current) doneRef.current();
    }

    function onKeyDown(e) {
      const answer = s.detector.key(e);
      if (answer.type === "start") {
        // keydown comes before the character lands: this is the field as it was
        s.snap = snapshotField(document.activeElement);
        return;
      }
      if (answer.type !== "scan") return;
      e.preventDefault();
      e.stopPropagation();
      restoreField(s.snap);
      s.snap = null;
      if (isRepeatScan(s.last, answer.parsed, e.timeStamp)) {
        s.last.at = e.timeStamp;
        return;
      }
      if (dialogOpen()) {
        setResult({ tone: "warn", title: "SCAN NOT USED", message: "Close the open window first, then scan again." });
        return;
      }
      s.last = { code: answer.parsed.code, at: e.timeStamp };
      if (!onRef.current) {
        setResult({ tone: "info", title: "Scanning is off", message: "Scanning isn't switched on at this station yet." });
        return;
      }
      handle(answer.parsed.code);
    }

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  if (!result) return null;
  const tone = TONE[result.tone] || TONE.warn;
  return (
    <div
      role="status"
      data-testid="scan-result"
      data-tone={result.tone}
      style={{
        position: "fixed", top: "1rem", left: "50%", transform: "translateX(-50%)", zIndex: 2100,
        width: "min(92vw, 1500px)", background: tone.bg, border: `5px solid ${tone.border}`, color: tone.color,
        borderRadius: 14, padding: "1rem 1.5rem", display: "flex", alignItems: "center", gap: "1.5rem",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "3rem", fontWeight: 900, lineHeight: 1.1 }}>{result.title}</div>
        {result.message && <div style={{ fontSize: "1.7rem", fontWeight: 700, marginTop: "0.3rem" }}>{result.message}</div>}
      </div>
      {result.tone === "bad" && (
        <button
          type="button"
          onClick={() => setResult(null)}
          style={{
            background: "white", color: "#111", border: "none", borderRadius: 10, fontWeight: 800,
            fontSize: "1.5rem", padding: "0.6rem 1.6rem", cursor: "pointer",
          }}
        >
          OK
        </button>
      )}
    </div>
  );
}

/** Beside a pallet's beacon: "SCAN BCN-012" until it is confirmed for this job, then a tick. */
export function PalletScanChip({ dest, size = "big" }) {
  const ctx = useContext(KioskScanContext);
  const state = palletScanState(dest, ctx);
  if (!state) return null;
  const big = size === "big";
  const done = state === "done";
  const scanned = done && dest.pallet_scan.method === "scan";
  return (
    <span
      data-testid="pallet-scan-chip"
      data-state={state}
      title={done ? `${scanned ? "Scanned" : "Confirmed without a scan"}${dest.pallet_scan.user_name ? ` · ${dest.pallet_scan.user_name}` : ""}` : "Scan this pallet's beacon once you have it"}
      style={{
        display: "inline-flex", alignItems: "center", borderRadius: 8, whiteSpace: "nowrap", fontWeight: 800,
        padding: big ? "0.15rem 0.6rem" : "0.05rem 0.45rem", fontSize: big ? "1.15rem" : "0.9rem",
        background: done ? "#12422a" : "#ffd969", color: done ? "#9ff0c2" : "#201600",
      }}
    >
      {done ? (scanned ? "✓ SCANNED" : "✓ CONFIRMED") : `SCAN ${dest.pallet_beacon_id}`}
    </span>
  );
}

function PalletScanRow({ pallet }) {
  const refresh = useContext(SurplusRefreshContext);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const onCantScan = async () => {
    const pin = await pinPrompt(
      `Your PIN, to confirm you have ${pallet.short_id || "this pallet"} (${pallet.beacon_id}) without a scan:`,
      { autofill: false },
    );
    if (!pin) return;
    setBusy(true);
    setNote(null);
    try {
      const out = await confirmPalletNoScan(pallet.pallet_id, pin);
      if (out.outcome === "ok" && out.task_id) stopPalletLight(out.task_id, pallet.pallet_id);
      refresh();
    } catch (e) {
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.25rem 0", borderTop: "1px solid #6b5a22" }}>
      <div style={{ flex: 1, minWidth: 0, fontSize: "1.25rem", fontWeight: 800, fontFamily: "monospace" }}>
        {pallet.short_id} · {pallet.beacon_id}{pallet.so_number ? ` · ${pallet.so_number}` : ""}
      </div>
      {note && <div role="alert" style={{ color: "#ff8a8a", fontSize: "1rem", fontWeight: 700 }}>{note}</div>}
      <button
        type="button"
        disabled={busy}
        onClick={onCantScan}
        style={{
          background: "#3a4250", color: "#e8ecf2", border: "none", borderRadius: 8, fontWeight: 800,
          fontSize: "1.05rem", padding: "0.35rem 0.9rem", cursor: "pointer", whiteSpace: "nowrap",
        }}
      >
        {busy ? "Saving…" : "Can't scan"}
      </button>
    </div>
  );
}

/** The live job's pallets not scanned yet. Nothing when there are none, or with the switch off. */
export function PalletScanStrip({ parts }) {
  const { scanOn } = useContext(KioskScanContext);
  const pallets = palletsToScan(parts, scanOn);
  if (!pallets.length) return null;
  return (
    <div
      data-testid="pallet-scan-strip"
      style={{
        background: "#3a3320", border: "3px solid #ffd969", borderRadius: 10, color: "#ffeeb0",
        padding: "0.5rem 0.9rem", flexShrink: 0,
      }}
    >
      <div style={{ fontSize: "1.35rem", fontWeight: 800, marginBottom: "0.25rem" }}>
        SCAN THE BEACON WHEN YOU FETCH THE PALLET ({pallets.length})
      </div>
      {pallets.slice(0, STRIP_MAX_ROWS).map((p) => <PalletScanRow key={p.pallet_id} pallet={p} />)}
      {pallets.length > STRIP_MAX_ROWS && (
        <div style={{ fontSize: "1rem", color: "#d9cc8a", marginTop: "0.2rem" }}>+ {pallets.length - STRIP_MAX_ROWS} more</div>
      )}
    </div>
  );
}
